import { Download } from "lucide-react";
import { useState, type ReactNode } from "react";

import {
  AGREEMENT_STATUS_LABEL,
  APPROVED_INTERNALLY_LABEL,
  QUOTE_STATUS_LABEL,
  TERMS_STATUS_LABEL,
  type BillingAgreementStatus,
} from "../../shared/billing-labels";
import type { BillingQuote, BillingTerms } from "../../shared/billing-schemas";
import { Badge } from "../components/ui/badge";
import { Button, buttonVariants } from "../components/ui/button";
import {
  agreementActions,
  isInternal,
  type Agreement,
  type AgreementActionKind,
} from "./billing-agreements";
import { quotePdfHref } from "./billing-api";
import { describeDiscount, formatDay, summarizeTotals } from "./billing-format";
import { Lines } from "./BillingLines";

/**
 * One item of the Quotes list (ADR-0077): what was offered or agreed, its state, and what may be
 * done next. The header says enough to act on; everything else waits behind Details.
 */

function who(person: { displayName: string } | null): string {
  return person?.displayName ?? "an employee no longer here";
}

function badgeVariant(
  status: BillingAgreementStatus,
): "default" | "secondary" | "outline" | "destructive" {
  if (status === "active") return "default";
  if (status === "sent" || status === "draft") return "secondary";
  return status === "declined" || status === "expired" ? "destructive" : "outline";
}

function Validity({ quote }: { quote: BillingQuote }) {
  if (quote.validUntil === null) return null;
  return (
    <span>
      {quote.status === "expired" ? " · expired " : " · accept by "}
      {formatDay(quote.validUntil)}
    </span>
  );
}

function Meta({ item }: { item: Agreement }) {
  const source = item.kind === "quote" ? item.quote : item.terms;
  return (
    <p className="text-xs text-muted-foreground">
      {source.tool ? source.tool.displayName : "Whole organization"} · effective{" "}
      {formatDay(source.effectiveAt)}
      {source.endsAt !== null ? ` → ${formatDay(source.endsAt)}` : ""}
      {item.kind === "quote" ? <Validity quote={item.quote} /> : null}
      {item.kind === "quote" && item.quote.supersedesTermsId !== null
        ? " · replaces the active terms"
        : ""}
      {item.kind === "quote" && item.quote.supersedesQuoteId !== null
        ? " · replaces an earlier quote"
        : ""}
      {item.kind === "terms" && item.terms.supersedesTermsId !== null ? " · revision" : ""}
    </p>
  );
}

function closedLine(label: string, terms: BillingTerms): string {
  return terms.closedAt === null
    ? ""
    : ` · ${label} ${formatDay(terms.closedAt)} by ${who(terms.closedBy)}`;
}

function QuoteProvenance({ quote, terms }: { quote: BillingQuote; terms: BillingTerms | null }) {
  const decided = quote.status === "accepted" || quote.status === "declined";
  const quoteLabel = QUOTE_STATUS_LABEL[quote.status].toLowerCase();
  return (
    <p className="mt-2 text-xs text-muted-foreground">
      Drafted {formatDay(quote.createdAt)} by {who(quote.createdBy)}
      {quote.sentAt !== null ? ` · sent ${formatDay(quote.sentAt)} by ${who(quote.sentBy)}` : ""}
      {decided && quote.decidedAt !== null
        ? ` · ${quoteLabel} ${formatDay(quote.decidedAt)} by ${quote.decidedBy?.displayName ?? "the customer"}`
        : ""}
      {decided && quote.decisionReason ? ` — “${quote.decisionReason}”` : ""}
      {terms !== null
        ? closedLine(TERMS_STATUS_LABEL[terms.status].toLowerCase(), terms)
        : quote.closedAt !== null
          ? ` · ${quoteLabel} ${formatDay(quote.closedAt)} by ${who(quote.closedBy)}`
          : ""}
    </p>
  );
}

function TermsProvenance({ terms }: { terms: BillingTerms }) {
  return (
    <p className="mt-2 text-xs text-muted-foreground">
      Drafted {formatDay(terms.createdAt)} by {who(terms.createdBy)}
      {terms.approvedAt !== null
        ? ` · approved ${formatDay(terms.approvedAt)} by ${who(terms.approvedBy)}`
        : ""}
      {closedLine(TERMS_STATUS_LABEL[terms.status].toLowerCase(), terms)}
    </p>
  );
}

/** What was proposed, what was said to the customer, and what was kept internal. */
function Details({ item }: { item: Agreement }) {
  const source = item.kind === "quote" ? item.quote : item.terms;
  const quote = item.kind === "quote" ? item.quote : null;
  return (
    <>
      {source.discount ? (
        <p className="text-xs text-muted-foreground">
          {quote ? "Quote discount" : "Terms discount"}: {describeDiscount(source.discount)}
        </p>
      ) : null}
      <div className="mt-2">
        <Lines terms={source} />
      </div>
      {quote?.message ? (
        <p className="mt-2 whitespace-pre-wrap text-sm">
          <span className="text-xs text-muted-foreground">To the customer: </span>
          {quote.message}
        </p>
      ) : null}
      {quote && quote.contacts.length > 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Billing contacts:{" "}
          {quote.contacts.map((contact) => `${contact.name} <${contact.email}>`).join(", ")}
        </p>
      ) : null}
      {source.notes ? (
        <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{source.notes}</p>
      ) : null}
      {item.kind === "quote" ? (
        <QuoteProvenance quote={item.quote} terms={item.terms} />
      ) : (
        <TermsProvenance terms={item.terms} />
      )}
    </>
  );
}

/**
 * The quote as a file, for sending on or filing. It is the customer's own copy — the same page
 * their Billing tab hands them — so a draft prints marked as one and nothing internal is on it.
 * Terms approved without a quote have no such document, so no link appears for them.
 */
function DownloadQuote({ customerSlug, item }: { customerSlug: string; item: Agreement }) {
  if (item.kind !== "quote") return null;
  return (
    <a
      className={buttonVariants({ variant: "ghost" })}
      href={quotePdfHref(customerSlug, item.quote.id)}
      aria-label={`Download the quote: ${item.quote.title}`}
    >
      <Download aria-hidden="true" /> PDF
    </a>
  );
}

export function BillingAgreementCard({
  customerSlug,
  item,
  canManage,
  defaultOpen,
  onAct,
  billing,
  signed = false,
}: {
  customerSlug: string;
  item: Agreement;
  canManage: boolean;
  /** How the item's approved terms are being billed, when they are in force. */
  billing?: ReactNode;
  /** Set up from a document the customer signed, rather than approved internally. */
  signed?: boolean;
  /** Current items open with their details; past ones keep them behind the toggle. */
  defaultOpen: boolean;
  onAct: (item: Agreement, action: AgreementActionKind) => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const source = item.kind === "quote" ? item.quote : item.terms;
  const actions = agreementActions(item, canManage);
  const detailsId = `agreement-${item.id}-details`;
  return (
    <li className="rounded-xl bg-card p-6 shadow-card" aria-label={source.title}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-lg font-bold">{source.title}</p>
          <Meta item={item} />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {signed ? <Badge variant="outline">Signed</Badge> : null}
          {!signed && item.kind === "terms" && isInternal(item.terms) ? (
            <Badge variant="outline">{APPROVED_INTERNALLY_LABEL}</Badge>
          ) : null}
          <Badge variant={badgeVariant(item.status)}>{AGREEMENT_STATUS_LABEL[item.status]}</Badge>
        </div>
      </div>
      <p className="mt-3 text-xl font-bold tabular-nums">{summarizeTotals(source.totals)}</p>
      {billing}
      <div className="mt-5 flex flex-wrap gap-1 border-t border-muted pt-4">
        {actions.map((offer) => (
          <Button
            key={offer.action}
            type="button"
            variant={offer.primary ? "default" : "ghost"}
            onClick={() => {
              onAct(item, offer.action);
            }}
          >
            {offer.label}
          </Button>
        ))}
        <DownloadQuote customerSlug={customerSlug} item={item} />
        <Button
          type="button"
          variant="ghost"
          aria-expanded={open}
          aria-controls={detailsId}
          onClick={() => {
            setOpen((current) => !current);
          }}
        >
          {open ? "Hide details" : "Details"}
        </Button>
      </div>
      {open ? (
        <div id={detailsId}>
          <Details item={item} />
        </div>
      ) : null}
    </li>
  );
}
